import { SceneObjectRenderer } from '@components/ViewerManager/SceneObjectRendererENC'
import { useGLBInstance } from '@hooks/useGLBInstance'
import { Text } from '@react-three/drei'
import { useAppStore } from '@store'
import { AnyID, LinkedItemSon } from '@types'
import React from 'react'
import { SelectionOutline } from '../Item/SelectionOutline'

type Props = {
  id: AnyID
  renderTarget?: any
  itemWidth?: number
  fatherId?: AnyID
  matrix?: any
}

export const LinkedItemSon3D = React.memo(function LinkedItemSon3D({
  id,
  renderTarget,
  itemWidth,
  fatherId,
}: Props) {
  const sonGroupRef = React.useRef<any>(null)
  const item = useAppStore((s) => s.items.get(id)) as LinkedItemSon | undefined
  const childs = item?.childs
  const textures = item?.textures
  const morphs = item?.morphs
  const zones = item?.zones
  const catalogID = item?.catalogID
  const glb = useGLBInstance(String(catalogID))
  const x = item?.x
  const y = item?.y
  const z = item?.z
  const visible = item?.visible
  const isTirador = item?.description === 'Tirador'

  const MULTIPLIER = 1000
  const getContiguousItems = useAppStore.use.getContItemsForEncimeras()
  const mode = useAppStore.use.mode()
  const verBisagras = useAppStore((s) => s.prefs.VERBISAGRAALZADO)
  const textoManoIZQ = useAppStore((s) => s.prefs.MANOIZQUIERDA)
  const textoManoDCH = useAppStore((s) => s.prefs.MANODERECHA)
  const colorTextoBisagra = useAppStore((s) => s.prefs.COLORTEXTOBISAGRA)
  const isParentSelected = useAppStore((s) =>
    fatherId ? s.layers[s.currentLayer].selected.has(fatherId as AnyID) : false,
  )
  const isSelfSelected = useAppStore((s) => s.layers[s.currentLayer].selected.has(id as AnyID))
  const getHandText = (handPosition: string | null) => {
    if (!handPosition || !item) return ''

    const parent = useAppStore.getState().items.get(item.firstParentID as AnyID)
    if (!parent) return ''

    const hinge = parent.hinge ? textoManoIZQ : textoManoDCH

    return hinge
  }

  const calculateZRotation = (item: LinkedItemSon | undefined) => {
    if (!item) return 0

    const parent = useAppStore.getState().items.get(item.firstParentID as AnyID)
    if (!parent) return 0

    let prefix = 'b'
    if (parent.tipoNormalizado === 'MAL' || parent.tipoNormalizado === 'COL') prefix = 'a'

    let rotationZ = 0
    if (item.handPosition === 'Izquierda') {
      rotationZ = (item as any)[`${prefix}iRotacionZ`] ?? 0
    } else if (item.handPosition === 'Centro') {
      rotationZ = (item as any)[`${prefix}cRotacionZ`] ?? 0
    } else if (item.handPosition === 'Derecha') {
      rotationZ = (item as any)[`${prefix}dRotacionZ`] ?? 0
    } else if (item.handPosition === 'Normal') {
      const parentItem = useAppStore.getState().items.get(item.firstParentID as AnyID)
      if (parentItem && (parentItem as any).childrenOrder) {
        const tiradores = (parentItem as any).childrenOrder
          .map((childOrder: string) => childOrder.split('!')[1])
          .filter((childId: string) => {
            const child = useAppStore.getState().items.get(childId as AnyID)
            return child && child.description === 'Tirador'
          })

        const tiradorIndex = tiradores.findIndex((childId: string) => childId === item.id)
        if (tiradorIndex === 0) {
          rotationZ = (item as any)[`${prefix}iRotacionZ`] ?? 0
        } else if (tiradorIndex === 1) {
          rotationZ = (item as any)[`${prefix}dRotacionZ`] ?? 0
        }
      }
    }
    if (rotationZ === 0) return 0

    return (rotationZ * Math.PI) / 180
  }

  const parent = useAppStore((s) =>
    item?.firstParentID ? s.items.get(item.firstParentID as AnyID) : undefined,
  )

  const hasAlternativePosition =
    item &&
    (item.aiPosicionX !== null ||
      item.aiPosicionY !== null ||
      item.aiPosicionZ !== null ||
      item.adPosicionX !== null ||
      item.adPosicionY !== null ||
      item.adPosicionZ !== null ||
      item.biPosicionX !== null ||
      item.biPosicionY !== null ||
      item.biPosicionZ !== null ||
      item.bdPosicionX !== null ||
      item.bdPosicionY !== null ||
      item.bdPosicionZ !== null)

  // Verificar si alguna rotación es 180
  const has180Rotation =
    item &&
    ((item as any).aiRotacionX === 180 ||
      (item as any).aiRotacionY === 180 ||
      (item as any).aiRotacionZ === 180 ||
      (item as any).adRotacionX === 180 ||
      (item as any).adRotacionY === 180 ||
      (item as any).adRotacionZ === 180 ||
      (item as any).acRotacionX === 180 ||
      (item as any).acRotacionY === 180 ||
      (item as any).acRotacionZ === 180 ||
      (item as any).biRotacionX === 180 ||
      (item as any).biRotacionY === 180 ||
      (item as any).biRotacionZ === 180 ||
      (item as any).bdRotacionX === 180 ||
      (item as any).bdRotacionY === 180 ||
      (item as any).bdRotacionZ === 180 ||
      (item as any).bcRotacionX === 180 ||
      (item as any).bcRotacionY === 180 ||
      (item as any).bcRotacionZ === 180 ||
      (item as any).a2idRotacionX === 180 ||
      (item as any).a2idRotacionY === 180 ||
      (item as any).a2idRotacionZ === 180 ||
      (item as any).b2idRotacionX === 180 ||
      (item as any).b2idRotacionY === 180 ||
      (item as any).b2idRotacionZ === 180)

  const showText =
    verBisagras &&
    isTirador &&
    mode === 'MODE_FORCE_ALZADOS' &&
    parent?.bisagra &&
    item?.handPosition &&
    hasAlternativePosition

  const textRotation: [number, number, number] = has180Rotation ? [0, 0, Math.PI] : [0, 0, 0]
  const textYPosition = has180Rotation ? -0.05 : -0.05
  const textXPosition = 0

  return (
    <group
      ref={sonGroupRef}
      name={id}
      position={[x! / MULTIPLIER, y! / MULTIPLIER, z! / MULTIPLIER]}
      rotation={[0, 0, calculateZRotation(item)]}
      visible={visible !== false}
      // rotation={ITEMS_REVERT_FIXED_ROTATION}
      scale={1}
    >
      {isSelfSelected && !isParentSelected ? (
        <SelectionOutline
          id={id as AnyID}
          width={item?.width || 0}
          height={item?.height || 0}
          thickness={item?.thickness || 0}
          parentGroupRef={sonGroupRef}
        />
      ) : null}
      <SceneObjectRenderer
        key={glb?.uuid}
        object={glb}
        zones={zones!}
        morphs={morphs!}
        textures={textures as any}
        renderTarget={renderTarget}
        childs={childs}
        depth={1}
        getContiguousItems={getContiguousItems}
        itemWidth={itemWidth}
        fatherId={fatherId}
      />
      {/* Texto de posición de bisagra para tiradores hijo */}
      {showText && (
        <Text
          position={[textXPosition, textYPosition, 0.3]}
          rotation={textRotation}
          fontSize={0.05}
          color={colorTextoBisagra}
          anchorX='center'
          anchorY='middle'
        >
          {getHandText((item as any).handPosition)}
        </Text>
      )}
      <group
        visible={visible !== false}
        // scale={ITEM_SCENE_SCALE}
      >
        {childs &&
          childs.size &&
          Array.from(childs ?? []).map((_id) => (
            <LinkedItemSon3D
              key={_id}
              id={_id}
            />
          ))}
      </group>
    </group>
  )
})
